"use client";

/**
 * 브랜드 입점 신청 페이지
 *
 * 입점 안내 + 신청 폼 (브랜드 정보, 제품 정보, 사업자등록증)
 */
import { useState } from "react";
import { useSearchParams } from "next/navigation";
import { useQuery } from "@tanstack/react-query";
import { CheckCircle, Store } from "lucide-react";
import { toast } from "sonner";

import { createBrandApplyService } from "@/shared/api/services/brandApplyService";
import { useBuyerService } from "@/shared/auth/hooks";

import { applyKeys } from "@/features/apply/queries/keys";

import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";

interface FormState {
  brandName: string;
  email: string;
  mobileNo: string;
  registrationNo: string;
  homepageUrl: string;
  mainProdLink: string;
  retailPrice: string;
  wholesalePrice: string;
  minOrderQty: string;
}

const INITIAL_FORM: FormState = {
  brandName: "",
  email: "",
  mobileNo: "",
  registrationNo: "",
  homepageUrl: "",
  mainProdLink: "",
  retailPrice: "",
  wholesalePrice: "",
  minOrderQty: "",
};

export function BrandApplyContent() {
  const searchParams = useSearchParams();
  const inviteType = searchParams.get("type") ?? "";
  const inviteId = searchParams.get("id") ?? "";
  const service = useBuyerService(createBrandApplyService);

  // Counters
  const userCountQuery = useQuery({
    queryKey: applyKeys.userCount(),
    queryFn: () => service.getUserCount(),
  });
  const brandCountQuery = useQuery({
    queryKey: applyKeys.brandCount(),
    queryFn: () => service.getBrandCount(),
  });
  const userCount = userCountQuery.data?.data?.count ?? 0;
  const brandCount = brandCountQuery.data?.data?.count ?? 0;

  const [form, setForm] = useState<FormState>(INITIAL_FORM);
  const [licenseFile, setLicenseFile] = useState<File | null>(null);
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [isSubmitted, setIsSubmitted] = useState(false);
  const [errors, setErrors] = useState<Partial<Record<keyof FormState | "license", string>>>({});

  function handleChange(field: keyof FormState, value: string) {
    setForm((prev) => ({ ...prev, [field]: value }));
    // Clear error on change
    if (errors[field]) {
      setErrors((prev) => Object.fromEntries(Object.entries(prev).filter(([k]) => k !== field)));
    }
  }

  const discountRate =
    Number(form.retailPrice) > 0 && Number(form.wholesalePrice) > 0
      ? Math.round(
          ((Number(form.retailPrice) - Number(form.wholesalePrice)) / Number(form.retailPrice)) *
            100,
        )
      : null;

  function validate(): boolean {
    const errs: Partial<Record<keyof FormState | "license", string>> = {};

    if (!form.brandName) errs.brandName = "브랜드명을 입력해주세요.";
    if (!form.email) {
      errs.email = "이메일을 입력해주세요.";
    } else if (!/^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(form.email)) {
      errs.email = "올바른 이메일 형식이 아닙니다.";
    }
    if (!form.mobileNo) {
      errs.mobileNo = "핸드폰 번호를 입력해주세요.";
    } else if (!/^\d{11}$/.test(form.mobileNo)) {
      errs.mobileNo = "핸드폰 번호는 숫자 11자리여야 합니다.";
    }
    if (!form.registrationNo) {
      errs.registrationNo = "사업자등록번호를 입력해주세요.";
    } else if (!/^\d{10,}$/.test(form.registrationNo)) {
      errs.registrationNo = "사업자등록번호는 숫자 10자리 이상이어야 합니다.";
    }
    if (!licenseFile) {
      errs.license = "사업자등록증을 첨부해주세요.";
    } else if (licenseFile.size > 20 * 1024 * 1024) {
      errs.license = "파일 크기가 20MB를 초과합니다. 파일을 줄여서 다시 첨부해주세요.";
    }

    setErrors(errs);
    return Object.keys(errs).length === 0;
  }

  async function handleSubmit(e: React.FormEvent) {
    e.preventDefault();
    if (isSubmitting) return;

    if (!validate()) return;

    setIsSubmitting(true);
    try {
      // Run independent validation checks concurrently
      const [existingRes, dupRes] = await Promise.all([
        service.getMyRequested({ type: "mobileNo", mobileNo: form.mobileNo }),
        service.checkDuplication({
          name: form.brandName,
          mobileNo: form.mobileNo,
          registrationNo: form.registrationNo,
          email: form.email,
        }),
      ]);

      if (existingRes.data?.apply) {
        toast.error("이미 입점 신청이 접수되어 있습니다.");
        setIsSubmitting(false);
        return;
      }

      const dupFields = Object.keys(dupRes.data ?? {});
      if (dupFields.length > 0) {
        toast.error(`중복된 정보가 있습니다: ${dupFields.join(", ")}`);
        setIsSubmitting(false);
        return;
      }

      // Upload license (validate() already ensures licenseFile is non-null)
      const formData = new FormData();
      if (licenseFile) formData.append("file", licenseFile);
      const uploadRes = await service.uploadLicense(formData);
      const licenseUrl = uploadRes.data?.url ?? "";

      // Submit application
      await service.apply({
        name: form.brandName,
        email: form.email,
        mobileNo: form.mobileNo,
        registrationNo: form.registrationNo,
        businessName: form.brandName,
        description: "",
        businessRegistrationImageUrl: licenseUrl,
        homepagUrl: form.homepageUrl || undefined,
        mainProdLink: form.mainProdLink || undefined,
        mainProdRetailPrice: form.retailPrice ? Number(form.retailPrice) : undefined,
        mainProdWholesalePrice: form.wholesalePrice ? Number(form.wholesalePrice) : undefined,
        mainProdMinOrderCnt: form.minOrderQty ? Number(form.minOrderQty) : undefined,
      });

      setIsSubmitted(true);
    } catch {
      toast.error("입점 신청에 실패했습니다. 잠시 후 다시 시도해주세요.");
    } finally {
      setIsSubmitting(false);
    }
  }

  // Success state
  if (isSubmitted) {
    return (
      <div className="max-w-screen-xl mx-auto px-4 py-8">
        <div className="flex flex-col items-center gap-6 text-center pt-32 pb-20">
          <div className="relative">
            <div className="absolute -inset-4 bg-green-50 rounded-full blur-xl" />
            <CheckCircle className="relative h-14 w-14 text-green-500" strokeWidth={1} />
          </div>
          <div className="space-y-2">
            <p className="text-lg font-medium">입점 신청이 완료되었습니다</p>
            <p className="text-sm text-muted-foreground max-w-[320px]">
              검토 후 입력하신 이메일로 결과를 안내드리겠습니다. 보통 2-3 영업일 소요됩니다.
            </p>
          </div>
          <Button variant="outline" size="sm" className="rounded-sm" asChild>
            <a href="/">홈으로 돌아가기</a>
          </Button>
        </div>
      </div>
    );
  }

  return (
    <div className="max-w-screen-xl mx-auto px-4 py-8">
      {/* Hero section */}
      <div className="text-center mb-12">
        <div className="flex justify-center mb-4">
          <div className="h-16 w-16 rounded-full bg-neutral-100 flex items-center justify-center">
            <Store className="h-8 w-8 text-neutral-500" />
          </div>
        </div>
        <h1 className="text-2xl font-heading font-semibold mb-2">단길 입점 신청</h1>
        <p className="text-muted-foreground max-w-md mx-auto mb-4">
          한국의 라이프스타일/패션 브랜드를 전문 바이어에게 소개하세요.
        </p>
        {(userCount > 0 || brandCount > 0) && (
          <div className="flex justify-center gap-8 text-sm">
            <div>
              <span className="font-semibold text-lg">{userCount.toLocaleString()}</span>
              <span className="text-muted-foreground ml-1">바이어</span>
            </div>
            <div>
              <span className="font-semibold text-lg">{brandCount.toLocaleString()}</span>
              <span className="text-muted-foreground ml-1">브랜드</span>
            </div>
          </div>
        )}
      </div>

      {/* Application form */}
      <form onSubmit={handleSubmit} className="max-w-2xl mx-auto space-y-8">
        {/* Brand info */}
        <section className="border rounded-md p-6 space-y-4">
          <h2 className="font-medium">브랜드 정보</h2>
          <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
            <div>
              <Label htmlFor="brandName" className="text-sm">
                브랜드명 *
              </Label>
              <Input
                id="brandName"
                value={form.brandName}
                onChange={(e) => handleChange("brandName", e.target.value)}
                className={`mt-1 ${errors.brandName ? "border-destructive" : ""}`}
              />
              {errors.brandName && (
                <p className="text-sm text-destructive mt-1">{errors.brandName}</p>
              )}
            </div>
            <div>
              <Label htmlFor="email" className="text-sm">
                이메일 *
              </Label>
              <Input
                id="email"
                type="email"
                value={form.email}
                onChange={(e) => handleChange("email", e.target.value)}
                className={`mt-1 ${errors.email ? "border-destructive" : ""}`}
              />
              {errors.email && <p className="text-sm text-destructive mt-1">{errors.email}</p>}
            </div>
            <div>
              <Label htmlFor="mobileNo" className="text-sm">
                핸드폰 번호 *
              </Label>
              <Input
                id="mobileNo"
                value={form.mobileNo}
                onChange={(e) => handleChange("mobileNo", e.target.value)}
                placeholder="01012345678"
                className={`mt-1 ${errors.mobileNo ? "border-destructive" : ""}`}
              />
              {errors.mobileNo && (
                <p className="text-sm text-destructive mt-1">{errors.mobileNo}</p>
              )}
            </div>
            <div>
              <Label htmlFor="registrationNo" className="text-sm">
                사업자등록번호 *
              </Label>
              <Input
                id="registrationNo"
                value={form.registrationNo}
                onChange={(e) => handleChange("registrationNo", e.target.value)}
                placeholder="1234567890"
                className={`mt-1 ${errors.registrationNo ? "border-destructive" : ""}`}
              />
              {errors.registrationNo && (
                <p className="text-sm text-destructive mt-1">{errors.registrationNo}</p>
              )}
            </div>
          </div>
          <div>
            <Label htmlFor="homepageUrl" className="text-sm">
              홈페이지 URL
            </Label>
            <Input
              id="homepageUrl"
              value={form.homepageUrl}
              onChange={(e) => handleChange("homepageUrl", e.target.value)}
              placeholder="https://"
              className="mt-1"
            />
          </div>
        </section>

        {/* Product info */}
        <section className="border rounded-md p-6 space-y-4">
          <h2 className="font-medium">제품 정보</h2>
          <div>
            <Label htmlFor="mainProdLink" className="text-sm">
              대표 제품 링크
            </Label>
            <Input
              id="mainProdLink"
              value={form.mainProdLink}
              onChange={(e) => handleChange("mainProdLink", e.target.value)}
              placeholder="https://"
              className="mt-1"
            />
          </div>
          <div className="grid grid-cols-1 sm:grid-cols-3 gap-4">
            <div>
              <Label htmlFor="retailPrice" className="text-sm">
                소매가 (원)
              </Label>
              <Input
                id="retailPrice"
                type="number"
                value={form.retailPrice}
                onChange={(e) => handleChange("retailPrice", e.target.value)}
                className="mt-1"
              />
            </div>
            <div>
              <Label htmlFor="wholesalePrice" className="text-sm">
                도매가 (원)
              </Label>
              <Input
                id="wholesalePrice"
                type="number"
                value={form.wholesalePrice}
                onChange={(e) => handleChange("wholesalePrice", e.target.value)}
                className="mt-1"
              />
            </div>
            <div>
              <Label htmlFor="minOrderQty" className="text-sm">
                최소 주문 수량
              </Label>
              <Input
                id="minOrderQty"
                type="number"
                value={form.minOrderQty}
                onChange={(e) => handleChange("minOrderQty", e.target.value)}
                className="mt-1"
              />
            </div>
          </div>
          {discountRate !== null && (
            <p className="text-sm">
              할인율:{" "}
              <span
                className={
                  discountRate < 40
                    ? "text-red-600 font-medium"
                    : discountRate <= 50
                      ? "text-green-600 font-medium"
                      : "text-blue-600 font-medium"
                }
              >
                {discountRate}%
              </span>
            </p>
          )}
        </section>

        {/* License upload */}
        <section className="border rounded-md p-6 space-y-4">
          <h2 className="font-medium">사업자등록증 *</h2>
          <div className="relative">
            <input
              type="file"
              accept=".pdf,.jpg,.jpeg,.png"
              onChange={(e) => {
                const file = e.target.files?.[0] ?? null;
                setLicenseFile(file);
                if (file && file.size > 20 * 1024 * 1024) {
                  setErrors((prev) => ({
                    ...prev,
                    license: "파일 크기가 20MB를 초과합니다. 파일을 줄여서 다시 첨부해주세요.",
                  }));
                } else if (errors.license) {
                  setErrors((prev) =>
                    Object.fromEntries(Object.entries(prev).filter(([k]) => k !== "license")),
                  );
                }
              }}
              className="absolute inset-0 cursor-pointer opacity-0"
            />
            <div
              className={`border-2 border-dashed rounded-md p-6 text-center ${errors.license ? "border-destructive" : ""}`}
            >
              {licenseFile ? (
                <div>
                  <p className="text-sm font-medium">{licenseFile.name}</p>
                  <p className="text-xs text-muted-foreground mt-1">
                    {(licenseFile.size / 1024 / 1024).toFixed(1)}MB / 20MB
                  </p>
                </div>
              ) : (
                <p className="text-sm text-muted-foreground">
                  클릭하여 파일을 선택하세요 (PDF, JPG, PNG, 최대 20MB)
                </p>
              )}
            </div>
          </div>
          {errors.license && <p className="text-sm text-destructive">{errors.license}</p>}
        </section>

        {/* Submit */}
        <Button
          type="submit"
          className="w-full h-12 rounded-sm bg-neutral-900 text-white text-[14px] font-medium"
          disabled={isSubmitting}
        >
          {isSubmitting ? "신청 중..." : "입점 신청하기"}
        </Button>
      </form>

      {/* FAQ section */}
      <div className="max-w-2xl mx-auto mt-16">
        <h2 className="font-medium mb-4">자주 묻는 질문</h2>
        <div className="border rounded-md divide-y text-sm">
          <div className="p-4">
            <p className="font-medium mb-1">수수료는 얼마인가요?</p>
            <p className="text-muted-foreground">판매 수수료는 15% + VAT입니다.</p>
          </div>
          <div className="p-4">
            <p className="font-medium mb-1">정산은 언제 되나요?</p>
            <p className="text-muted-foreground">
              매월 영업일 기준 첫째 날에 전월 배송 완료 건에 대해 정산됩니다.
            </p>
          </div>
          <div className="p-4">
            <p className="font-medium mb-1">배송은 어떻게 처리하나요?</p>
            <p className="text-muted-foreground">
              브랜드에서 직접 배송합니다. 주문 접수 후 영업일 기준 3일 이내 출고해주세요.
            </p>
          </div>
        </div>
      </div>
    </div>
  );
}
